import type { BaseItem } from "~/models/Content/BaseItem"
import type { ContentProductPage } from "~/models/Content/ContentProductPage"
import type { ContentProductPageBlocks } from "~/models/Content/ContentProductPageBlocks"
import type { ContentProductPageMediaTypes, MediaType } from "~/models/Content/ContentProductPageMediaTypes"
import type { ContentProductPageUsp } from "~/models/Content/ContentProductPageUsp"
import type { ProductVariants } from "~/models/Content/ContentProducts"
import type { MathTaskIbexa, MathTaskIbexaSolution, MathTaskIbexaSuggestedSolution, MathTaskIbexaSuggestedSolutionSection } from "~/models/Content/schemas"
import type {
  SearchItem,
  SearchResult,
  Tag,
} from "~/models/Content/SearchResult"
import type {
  Article,
  ExplainerRelation,
  LegalSourceRelation,
  PersonRelation,
  SourceRelation,
  TermRelation,
  WhatIsRelation,
} from "~/types/article"
import type { Folder } from "~/types/folder"
import type {
  MarketingArticleContent,
  MarketingArticleSectionContent,
} from "~/types/MarketingArticle"
import type { Audio, Caption, Quiz, Video } from "~/types/media"
import type { PageData, PageSection } from "~/types/pages"

import type { Subject, SubjectPage } from "~/types/subject"
import { parse } from "valibot"

import { MEDIA_TYPE_ICONS } from "~/models/Content/ContentProductPageMediaTypes"
import { ContentType } from "~/models/Content/ContentType"
// Add schema imports
import {
  ArticleSchema,
  AudioSchema,
  BaseItemSchema,
  CaptionSchema,
  ChapterPageSchema,
  ChapterSchema,
  ContentProductPageBlocksSchema,
  ContentProductPageMediaTypesSchema,
  ContentProductPagesSchema,
  ContentProductPageUspSchema,
  ContributorSchema,
  ExplainerSidenoteSchema,
  FileSchema,
  FolderSchema,
  LegalSourceSidenoteSchema,
  MarketingArticleSchema,
  MarketingArticleSectionSchema,
  MathTaskIbexaSchema,
  MathTaskIbexaSolutionSchema,
  MathTaskIbexaSuggestedSolutionSchema,
  MathTaskIbexaSuggestedSolutionSectionSchema,
  PageSchema,
  PageSectionSchema,
  PersonSidenoteSchema,
  ProductContributorSchema,
  ProductVariantsSchema,
  QuizSchema,
  SourceSidenoteSchema,
  TermSidenoteSchema,
  VideoSchema,
  WhatIsSidenoteSchema,
} from "~/models/Content/schemas"

// Main mapping function
export function mapContents<T extends ContentType>(result: SearchResult): MappedIbexaContent<T>[] {
  if (!result.items.length) {
    return []
  }

  return result.items.map((searchItem) => {
    let mappedItem = {} as BaseItem
    try {
      // Combined mapping and validation
      switch (searchItem.contentType) {
        case ContentType.Product:
          mappedItem = mapProduct(searchItem)
          parse(ProductVariantsSchema, mappedItem)
          break
        case ContentType.ProductPage:
          mappedItem = mapProductPage(searchItem)
          parse(ContentProductPagesSchema, mappedItem)
          break
        case ContentType.ProductPageBlock:
          mappedItem = mapProductPageBlock(searchItem)
          parse(ContentProductPageBlocksSchema, mappedItem)
          break
        case ContentType.ProductPageUSP:
          mappedItem = mapProductPageUSP(searchItem)
          parse(ContentProductPageUspSchema, mappedItem)
          break
        case ContentType.ProductPageMediaTypes:
          mappedItem = mapProductPageMediaTypes(searchItem)
          parse(ContentProductPageMediaTypesSchema, mappedItem)
          break
        case ContentType.Article:
          mappedItem = mapArticle(searchItem)
          parse(ArticleSchema, mappedItem)
          break
        case ContentType.Video:
          mappedItem = mapVideo(searchItem)
          parse(VideoSchema, mappedItem)
          break
        case ContentType.Quiz:
          mappedItem = mapQuiz(searchItem)
          parse(QuizSchema, mappedItem)
          break
        case ContentType.Audio:
          mappedItem = mapAudio(searchItem)
          parse(AudioSchema, mappedItem)
          break
        case ContentType.SidenoteSource:
          mappedItem = mapSidenoteSource(searchItem)
          parse(SourceSidenoteSchema, mappedItem)
          break
        case ContentType.SidenoteTerm:
          mappedItem = mapSidenoteTerm(searchItem)
          parse(TermSidenoteSchema, mappedItem)
          break
        case ContentType.SidenotePerson:
          mappedItem = mapSidenotePerson(searchItem)
          parse(PersonSidenoteSchema, mappedItem)
          break
        case ContentType.Sidenote:
        case ContentType.SidenoteExplainer:
          mappedItem = mapSidenoteExplainer(searchItem)
          parse(ExplainerSidenoteSchema, mappedItem)
          break
        case ContentType.SidenoteLegalSource:
          mappedItem = mapSidenoteLegalSource(searchItem)
          parse(LegalSourceSidenoteSchema, mappedItem)
          break
        case ContentType.SidenoteWhatIs:
          mappedItem = mapSidenoteWhatIs(searchItem)
          parse(WhatIsSidenoteSchema, mappedItem)
          break
        case ContentType.MathTaskIbexa:
          mappedItem = mapMathTaskIbexa(searchItem)
          parse(MathTaskIbexaSchema, mappedItem)
          break
        case ContentType.SolutionIbexa:
          mappedItem = mapIbexaSolution(searchItem)
          parse(MathTaskIbexaSolutionSchema, mappedItem)
          break
        case ContentType.SuggestedSolutionIbexa:
          mappedItem = mapMathTaskIbexaSuggestedSolution(searchItem)
          parse(MathTaskIbexaSuggestedSolutionSchema, mappedItem)
          break
        case ContentType.SuggestedSolutionSectionIbexa:
          mappedItem =
            mapMathTaskIbexaSuggestedSolutionSection(searchItem)
          parse(
            MathTaskIbexaSuggestedSolutionSectionSchema,
            mappedItem,
          )
          break
        case ContentType.Image:
          mappedItem = mapImage(searchItem)
          break
        case ContentType.Folder:
          mappedItem = mapFolder(searchItem)
          parse(FolderSchema, mappedItem)
          break
        case ContentType.Chapter:
          mappedItem = mapChapter(searchItem)
          parse(ChapterSchema, mappedItem)
          break
        case ContentType.ChapterPage:
          mappedItem = mapChapterPage(searchItem)
          parse(ChapterPageSchema, mappedItem)
          break
        case ContentType.MarketingArticle:
          mappedItem = mapMarketingArticle(searchItem)
          parse(MarketingArticleSchema, mappedItem)
          break
        case ContentType.MarketingArticleSection:
          mappedItem = mapMarketingArticleSection(searchItem)
          parse(MarketingArticleSectionSchema, mappedItem)
          break
        case ContentType.Page:
          mappedItem = mapPage(searchItem)
          parse(PageSchema, mappedItem)
          break
        case ContentType.PageSection:
          mappedItem = mapPageSection(searchItem)
          parse(PageSectionSchema, mappedItem)
          break
        case ContentType.Caption:
          mappedItem = mapCaption(searchItem)
          parse(CaptionSchema, mappedItem)
          break
        case ContentType.File:
          mappedItem = mapFile(searchItem)
          parse(FileSchema, mappedItem)
          break
        case ContentType.ProductContributor:
          mappedItem = mapProductContributor(searchItem)
          parse(ProductContributorSchema, mappedItem)
          break
        case ContentType.Contributor:
          mappedItem = mapContributor(searchItem)
          parse(ContributorSchema, mappedItem)
          break
        default:
          mappedItem = mapBaseFields(searchItem)
          console.warn(
            `[Mapping] No mapping found for content type: ${searchItem.contentType}`,
          )
          parse(BaseItemSchema, mappedItem)
          break
      }

      // Validate the mapped item based on its content type
    } catch (error) {
      // Extract validation issues from ValiError
      let issues: Array<{ path?: any[], message: string }> = []

      if (error instanceof Error && "issues" in error) {
        issues = (error as any).issues
      }

      // Format validation errors
      const validationErrors = issues.map(issue => ({
        locationId: searchItem.metadata.locationId,
        contentId: searchItem.id,
        field: issue.path?.[0]?.key || "unknown field",
        message: issue.message,
        receivedValue: issue.path?.[0]?.value || "undefined",
      }))

      console.error(
        `Validation failed for content type ${searchItem.contentType}:`,
        validationErrors,
        error,
      )

      captureException(error, {
        extra: {
          contentType: searchItem.contentType,
          itemId: searchItem.id,
          validationErrors,
        },
      })
    }
    return mappedItem as MappedIbexaContent<T>
  })
}

// Base mapper function
function mapBaseFields(searchItem: SearchItem): BaseItem {
  const { fields, metadata } = searchItem

  return {
    title: fields.title?.value || "",
    shortTitle: fields.short_title?.value || "",
    contentId: searchItem.id,
    remoteId: metadata.remoteId,
    locationId: metadata.locationId,
    mainLocationId: metadata.mainLocationId,
    modified: metadata.modified || "",
    parentLocationId: metadata.parentLocationId,
    contentTypeIdentifier: searchItem.contentType,
    pathString: metadata.pathString,
    publishedDate: metadata.published,
    versionNo: metadata.versionNo,
    label: fields.label?.value || "",
    thumbnail: fields.thumbnail?.value?.src || "",
    thumbnailAlt: fields.thumbnail?.value?.alt || "",
    sortField: metadata.sortField,
    sortOrder: metadata.sortOrder,
    priority: metadata.priority,
    relationsIds: searchItem.relationIds,
    children: [], // Are populated in the getContents file
    relatedItems: [], // Are populated in the getContents file based on the relationsIds
    childCount: metadata.childCount || 0,
  }
}

// Content type specific mappers
function mapProduct(searchItem: SearchItem): ProductVariants {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  // TODO: this logic is for backwards compatibility and once data has been
  // migrated, we can remove this
  const oldStatus = fields.status?.value[0]?.value
  let newStatus = fields.status?.value[0]?.key || "in-development"
  if (oldStatus) {
    switch (oldStatus) {
      case "Coming Soon":
        captureMessage(
          '[Status Migration] Found outdated status "Coming Soon"',
          {
            level: "warning",
            extra: {
              productId: searchItem.id,
              oldStatus: "Coming Soon",
              newStatus: "coming-soon",
            },
          },
        )
        newStatus = "coming-soon"
        break
      case "Full Release":
        captureMessage(
          '[Status Migration] Found outdated status "Full Release"',
          {
            level: "warning",
            extra: {
              productId: searchItem.id,
              oldStatus: "Full Release",
              newStatus: "full-release",
            },
          },
        )
        newStatus = "full-release"
        break
      case "Partial Release":
        captureMessage(
          '[Status Migration] Found outdated status "Partial Release"',
          {
            level: "warning",
            extra: {
              productId: searchItem.id,
              oldStatus: "Partial Release",
              newStatus: "partial-release",
            },
          },
        )
        newStatus = "partial-release"
        break
    }
  }

  return {
    ...baseFields,
    ibexaTitle: fields.ibexa_title?.value || "",
    titleHtml: fields.title_html?.value,
    ean: fields.ean?.value,
    pensumFor: fields.pensum_for?.value || "",
    institute:
      fields.institute?.value.map((tag: Tag) => tag.value) || [],
    status: newStatus,
    _location: {
      id: searchItem.metadata.locationId,
      pathString: searchItem.metadata.pathString,
    },
    isFree: fields.is_free?.value ?? false,
  }
}

function mapProductPage(searchItem: SearchItem): ContentProductPage {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    header: {
      html5: fields.header?.value || "",
    },
    quote: fields.quote?.value || "",
    groupSalesPitch: {
      html5: fields.group_sales_pitch?.value || "",
    },
    theme: fields.theme?.value[0]?.value || "",
    blocks: [], // this is a different content type which should be higher in the stack
    uspList: [],
    productGroupStatusDetails:
      fields.product_group_status_details?.value || "",
  }
}

function mapProductPageBlock(searchItem: SearchItem): ContentProductPageBlocks {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem
  return {
    ...baseFields,
    body: fields.body?.value || "",
    header: fields.header?.value || "",
    imageText: fields.image_text?.value || "",
    image: fields.image?.value || null,
    blockOrientation:
      fields.block_orientation?.value?.[0]?.value || "",
  }
}

function mapProductPageUSP(searchItem: SearchItem): ContentProductPageUsp {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    uspList: [
      fields.usp_1?.value || "",
      fields.usp_2?.value || "",
      fields.usp_3?.value || "",
      fields.usp_4?.value || "",
    ],
  }
}

function mapProductPageMediaTypes(searchItem: SearchItem): ContentProductPageMediaTypes {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  const mappedTags = fields.product_media_types?.value.map(
    (tag: Tag) => ({
      text: tag.value,
      icon: MEDIA_TYPE_ICONS[tag.key as MediaType],
    }),
  )

  return {
    ...baseFields,
    productMediaTypes: mappedTags,
  }
}

function mapArticle(searchItem: SearchItem): Article {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem
  return {
    ...baseFields,
    id: baseFields.contentId,
    template: fields.template?.value || "",
    titleHtml: fields.title_html?.value || undefined,
    img: fields.image?.value || null,
    imageCaption: {
      html: fields.imageCaption?.value || "",
    },
    imageSize: fields.image_size?.value as
    | "Large"
    | "Medium"
    | "Small"
    | undefined,
    intro: {
      html: fields.intro?.value || "",
    },
    body: {
      html: fields.body?.value || "",
    },
    bg: fields.bg?.value || undefined,
    color: fields.color?.value || undefined,
    hasPictureFrame: fields.has_picture_frame?.value || undefined,
    pictureColor: fields.picture_color?.value[0]?.value || undefined,
    pictureText: {
      html: fields.picture_text?.value || "",
    },
    type: baseFields.contentTypeIdentifier,
  }
}

function mapVideo(searchItem: SearchItem): Video {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    id: baseFields.contentId.toString(),
    title: fields.title?.value || "",
    previewTitle: fields.previewTitle?.value || "",
    img: fields.image?.value || null,
    publishedBy: fields.publishedBy?.value || "",
    published: fields.published?.value || "",
    timestamps: (fields.timestamps?.value?.entries || []).map(
      (stamp: any, key: number) => ({
        id: `${baseFields.contentId}-video-timestamp-${key}`,
        ...stamp,
      }),
    ),
    embedCode: fields.embed_code?.value || "",
    transcription: {
      html: fields.transcription?.value || "",
    },
    metadata: fields.metadata?.value,
    _type: {
      identifier: searchItem.contentType || "",
    },
    captions: [],
  }
}

function mapAudio(searchItem: SearchItem): Audio {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem
  return {
    ...baseFields,
    title: fields.title?.value || "",
    shortTitle: fields.shortTitle?.value || "",
    caption: fields.caption?.value || "",
    timestamps: fields.timestamps?.value?.entries || [],
    id: baseFields.contentId.toString(),
    metadata: fields.metadata?.value,
  }
}

function mapQuiz(searchItem: SearchItem): Quiz {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem
  return {
    ...baseFields,
    id: fields.quiz_id?.value || "",
    title: fields.title?.value || "",
  }
}

function mapBaseImage(image: any) {
  if (!image)
    return undefined
  return {
    id: null,
    src: image.src || "",
    alt: image.alt || "",
    width: image.width || undefined,
    height: image.height || undefined,
    focalPointX: image.focalPointX || undefined,
    focalPointY: image.focalPointY || undefined,
  }
}

function mapBaseSidenoteFields(fields: any) {
  return {
    identifier: fields.identifier?.value || "",
    title: fields.title?.value || "",
    html: fields.html?.value || null,
    resourceUrl: fields.resourceUrl?.value || "",
    image: fields.image?.value || undefined,
    imageCaption: fields.imageCaption?.value,
    video: fields.video?.value?.contentIds || [],
    appendText: fields.appendText?.value,
  }
}

function mapSidenoteSource(searchItem: SearchItem): SourceRelation {
  const { fields } = searchItem
  const baseFieldsSideNotes = mapBaseSidenoteFields(fields)
  const baseFields = mapBaseFields(searchItem)

  return {
    ...baseFields,
    ...baseFieldsSideNotes,
    identifier: "source",
    resource: {
      type: fields.type?.value || "",
      author: fields.author?.value || "",
      title: fields.title?.value || "",
      edition: fields.edition?.value,
      publisher: fields.publisher?.value,
      publishYear: fields.publish_year?.value
        ? Number(fields.publish_year.value)
        : undefined,
      publishDate: fields.publish_date?.value
        ? fields.publish_date.value
        : null,
      url: fields.url?.value?.link || undefined,
    },
  }
}

function mapSidenoteLegalSource(searchItem: SearchItem): LegalSourceRelation {
  const { fields } = searchItem
  const baseFieldsSideNotes = mapBaseSidenoteFields(fields)
  const baseFields = mapBaseFields(searchItem)

  return {
    ...baseFields,
    ...baseFieldsSideNotes,
    identifier: "legal_source",
    resource: {
      type: fields.type?.value?.[0]?.value,
      text: fields.text?.value || "",
      title: fields.title?.value || "",
      url: fields.url?.value || undefined,
      paragraphAndSection: fields.paragraph_and_section?.value || "",
      urlTitle: fields.url_title?.value || "",
      typeDescription: fields.type_description?.value || "",
    },
  }
}

function mapSidenoteTerm(searchItem: SearchItem): TermRelation {
  const { fields } = searchItem
  const baseFieldsSideNotes = mapBaseSidenoteFields(fields)
  const baseFields = mapBaseFields(searchItem)

  return {
    ...baseFields,
    ...baseFieldsSideNotes,
    identifier: "term",
    resource: {
      definition: fields.definition?.value || "",
      explanation: fields.explanation?.value || "",
      isFlashcard: fields.isFlashcard?.value || false,
    },
  }
}

function mapSidenotePerson(searchItem: SearchItem): PersonRelation {
  const { fields } = searchItem
  const baseFieldsSideNotes = mapBaseSidenoteFields(fields)
  const baseFields = mapBaseFields(searchItem)

  return {
    ...baseFields,
    ...baseFieldsSideNotes,
    title: fields.name?.value || "",
    identifier: "person",
    resource: {
      birthYear: fields.birth_year?.value ?? 0,
      deceasedYear: fields.deceased_year?.value ?? undefined,
      about: fields.about?.value || "",
    },
  }
}

function mapSidenoteExplainer(searchItem: SearchItem): ExplainerRelation {
  const { fields } = searchItem
  const baseFieldsSideNotes = mapBaseSidenoteFields(fields)
  const baseFields = mapBaseFields(searchItem)

  return {
    ...baseFields,
    ...baseFieldsSideNotes,
    identifier:
      fields.identifier?.value === "explainer"
        ? "explainer"
        : "sidenote",
    html: fields.richtext?.value || "",
  }
}

function mapSidenoteWhatIs(searchItem: SearchItem): WhatIsRelation {
  const { fields } = searchItem
  const baseFieldsSideNotes = mapBaseSidenoteFields(fields)
  const baseFields = mapBaseFields(searchItem)

  return {
    ...baseFields,
    ...baseFieldsSideNotes,
    identifier: "what_is",
    resource: {
      explanation: fields.explanation?.value || "",
    },
  }
}

function mapMathTaskIbexa(searchItem: SearchItem): MathTaskIbexa {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem
  return {
    ...baseFields,
    assignmentType: fields.assignment_type?.value || "",
    assignmentText: {
      html: fields.assignment_text?.value || "",
    },
  }
}

function mapIbexaSolution(searchItem: SearchItem): MathTaskIbexaSolution {
  const { fields } = searchItem
  const baseFields = mapBaseFields(searchItem)
  return {
    ...baseFields,
    solutionTitle: fields.solution_title?.value || "",
    solutionText: {
      html: fields.solution_text?.value || "",
    },
  }
}

function mapMathTaskIbexaSuggestedSolution(searchItem: SearchItem): MathTaskIbexaSuggestedSolution {
  const { fields } = searchItem
  const baseFields = mapBaseFields(searchItem)
  return {
    ...baseFields,
    title: fields.title?.value || "",
  }
}

function mapMathTaskIbexaSuggestedSolutionSection(searchItem: SearchItem): MathTaskIbexaSuggestedSolutionSection {
  const { fields } = searchItem
  const baseFields = mapBaseFields(searchItem)
  return {
    ...baseFields,
    sectionBody: {
      html: fields.section_body?.value || "",
    },
    sectionImage: mapBaseImage(fields.section_image?.value),
  }
}

function mapImage(searchItem: SearchItem) {
  const { fields } = searchItem
  const baseFields = mapBaseFields(searchItem)
  return {
    ...baseFields,
    ...(mapBaseImage(fields?.image?.value) ?? {}),
    detailedDescription:
      fields.detailed_image_description?.value || "",
  }
}

function mapFolder(searchItem: SearchItem): Folder {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    id: baseFields.locationId,
    colorTag: fields.color_tag?.value[0]?.value || "",
    titleHtml: fields.title_html?.value || "",
    img: mapBaseImage(fields.image?.value),
    hidden: fields.hidden?.value || false,
    type: searchItem.contentType,
  }
}

function mapChapter(searchItem: SearchItem): Subject {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    id: baseFields.locationId,
    hidden: fields.hidden?.value || false,
    subjectPageTitlePosition:
      fields.subject_page_title_position?.value,
    introPages: [], // will be mapped higher up in the chain
    colorTag: fields.color_tag?.value[0]?.value || "",
    chapterType: fields.chapter_type?.value || "Kun fargefyll",
    description: fields.description?.value || "",
    rawHref: "",
    href: "",
    img: mapBaseImage(fields.image?.value),
    creditingIds: fields.crediting?.value?.contentIds || [],
    totalChildren: baseFields.childCount,
    type: searchItem.contentType,
    introPagesContentIds: fields.intro_pages?.value?.contentIds || [],
    isFree: fields.is_free?.value ?? false,
  }
}

function mapChapterPage(searchItem: SearchItem): SubjectPage {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    id: baseFields.locationId,
    title: fields.title?.value || "",
    template: fields.template?.value || "",
    richtext: fields.richtext?.value
      ? {
          html: fields.richtext.value,
        }
      : undefined,
    colorTheme: fields.color_theme?.value?.[0]?.value || "",
    image: mapBaseImage(fields.image?.value),
  }
}

function mapMarketingArticle(searchItem: SearchItem): MarketingArticleContent {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    subtitle: {
      html5: fields.subtitle?.value || "",
    },
    tags: fields.tags?.value || [],
  }
}

function mapMarketingArticleSection(searchItem: SearchItem): MarketingArticleSectionContent {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    id: baseFields.locationId.toString(),
    sectionBody: {
      html5: fields.section_body?.value || "",
    },
    sectionBodyIsAQuote:
      fields.section_body_is_a_quote?.value || false,
    sectionTitle: fields.section_title?.value || "",
    buttonText: fields.button_text?.value || "",
    buttonUrl: fields.button_url?.value || "",
    image: mapBaseImage(fields.image?.value),
  }
}

function mapPage(searchItem: SearchItem): PageData {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    id: baseFields.locationId,
    urlAliases: fields.url_aliases?.value || [],
    route: {
      path: fields.route_path?.value || "",
      name: fields.route_name?.value || "",
    },
    title: fields.title?.value || "",
    subtitle: fields.subtitle?.value || "",
    metaImage: mapBaseImage(fields.meta_image?.value),
    metaTitle: fields.meta_title?.value || "",
    metaDescription: fields.meta_description?.value || "",
    layout: fields.layout?.value || "",
  }
}

function mapPageSection(searchItem: SearchItem): PageSection {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    id: baseFields.locationId,
    image: fields.image?.value
      ? {
          src: fields.image.value.src || undefined,
          alt: fields.image.value.alt || undefined,
        }
      : undefined,
    richtext: fields.richtext?.value || "",
  }
}

function mapCaption(searchItem: SearchItem): Caption {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    captionFile: {
      uri: fields.caption_file?.value?.uri || "",
      fileName: fields.caption_file?.value?.fileName || "",
    },
    language: fields.language?.value?.[0]?.value || "",
  }
}

function mapFile(searchItem: SearchItem) {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    name: fields.name?.value || "",
    description: fields.description?.value || "",
    binaryFile: {
      fileName: fields.file?.value?.fileName || "",
      fileSize: fields.file?.value?.fileSize || 0,
      mimeType: fields.file?.value?.mimeType || "",
      id: fields.file?.value?.id || "",
      uri: fields.file?.value?.uri || "",
      url: fields.file?.value?.url || "",
      downloadCount: fields.file?.value?.downloadCount || 0,
    },
  }
}

function mapProductContributor(searchItem: SearchItem) {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    isMainAuthor: fields.main_author?.value || false,
  }
}

function mapContributor(searchItem: SearchItem) {
  const baseFields = mapBaseFields(searchItem)
  const { fields } = searchItem

  return {
    ...baseFields,
    image: fields.image?.value || undefined,
    firstname: fields.firstname?.value || "",
    lastname: fields.lastname?.value || "",
    role: fields.role?.value || "",
    bio: fields.bio
      ? {
          html: fields.bio?.value || "",
        }
      : undefined,
  }
}
